import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Search, Loader2, Layers } from "@/icons/lucide-compat";
import { ToolcraftText as Text } from "@openreel/ui";
import { ToolcraftTextInputControl } from "@openreel/ui";
import { useEngineStore } from "../../stores/engine-store";
import {
  SOCIAL_MEDIA_CATEGORY_INFO,
  type SocialMediaCategory,
  type ScriptableTemplate,
  type Clip,
} from "@openreel/core";
import { templateCloudService } from "../../services/template-cloud-service";
import { CategoryTabs } from "./CategoryTabs";
import { TemplateCard } from "./TemplateCard";
import { TemplatePreviewModal } from "./TemplatePreviewModal";

interface PlaceholderClip extends Clip {
  isPlaceholder?: boolean;
  placeholderId?: string;
}

interface TemplateGalleryProps {
  onTemplateApplied?: () => void;
  /**
   * Compact mode for the Home view: no page heading, no search, no category
   * tabs, just the first few cards. The whole gallery is reused rather than a
   * second grid built beside it, so loading, preview and apply stay in one
   * place and cannot drift.
   */
  compact?: boolean;
  /** Compact only: how many cards to show. */
  limit?: number;
  /** Compact only: rendered beside the section heading, for "See all". */
  headerAction?: React.ReactNode;
  /**
   * Compact only: leave the heading to the caller. The dashboard draws its own
   * strip headers so both strips share one type scale.
   */
  hideHeader?: boolean;
}

export const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  onTemplateApplied,
  compact = false,
  limit = 4,
  headerAction,
  hideHeader = false,
}) => {
  const getTemplateEngine = useEngineStore((state) => state.getTemplateEngine);

  const [templates, setTemplates] = useState<ScriptableTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<
    SocialMediaCategory | "all"
  >("all");
  const [selectedTemplate, setSelectedTemplate] =
    useState<ScriptableTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        const templateEngine = await getTemplateEngine();
        await templateEngine.initialize();
        const builtinTemplates = templateEngine.getBuiltinTemplates();
        const cloudTemplates =
          await templateCloudService.listScriptableTemplates();

        const allTemplates = [
          ...(builtinTemplates.map((t) => {
            const placeholderClipMap = new Map<
              string,
              { clipId: string; trackId: string }
            >();
            for (const track of t.timeline.tracks) {
              for (const clip of track.clips) {
                const pClip = clip as PlaceholderClip;
                if (pClip.isPlaceholder && pClip.placeholderId) {
                  placeholderClipMap.set(pClip.placeholderId, {
                    clipId: clip.id,
                    trackId: track.id,
                  });
                }
              }
            }

            return {
              ...t,
              placeholders: t.placeholders.map((p) => {
                const clipInfo = placeholderClipMap.get(p.id);
                return {
                  ...p,
                  targets: clipInfo
                    ? [
                        {
                          clipId: clipInfo.clipId,
                          trackId: clipInfo.trackId,
                          // A media slot has to name a property that exists.
                          // Everything was pointed at "content", which is not a
                          // field on Clip, so a chosen file was written to a
                          // property nothing reads and the slot stayed empty.
                          // Text is unaffected: it is rebuilt from the separate
                          // placeholder-clip pass, not from this write.
                          // Built-in placeholders are only ever text, subtitle
                          // or media, so media is the whole of the check here.
                          property: p.type === "media" ? "mediaId" : "content",
                        },
                      ]
                    : [],
                  defaultValue: p.defaultValue as unknown,
                };
              }),
              socialCategory: mapCategoryToSocial(t.category),
            };
          }) as ScriptableTemplate[]),
          ...cloudTemplates,
        ];

        const unique = Array.from(
          new Map(allTemplates.map((t) => [t.id, t])).values(),
        );

        setTemplates(unique);
      } catch (error) {
        console.error("Failed to load templates:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [getTemplateEngine]);

  const filteredTemplates = useMemo(() => {
    let result = templates;

    if (selectedCategory !== "all") {
      result = result.filter(
        (t) =>
          t.socialCategory === selectedCategory ||
          t.category === selectedCategory,
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query) ||
          t.tags.some((tag) => tag.toLowerCase().includes(query)),
      );
    }

    return result;
  }, [templates, selectedCategory, searchQuery]);

  const handleSelectTemplate = useCallback((template: ScriptableTemplate) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
    setSelectedTemplate(null);
  }, []);

  const handleApplyTemplate = useCallback(() => {
    handleClosePreview();
    onTemplateApplied?.();
  }, [handleClosePreview, onTemplateApplied]);

  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = { all: templates.length };
    for (const category of SOCIAL_MEDIA_CATEGORY_INFO) {
      stats[category.id] = templates.filter(
        (t) => t.socialCategory === category.id || t.category === category.id,
      ).length;
    }
    return stats;
  }, [templates]);

  if (isLoading && compact) {
    // Home shows a quiet placeholder rather than a page-sized spinner.
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-hidden>
        {Array.from({ length: limit }, (_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-bg-2" />
        ))}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
          <Loader2 className="relative w-10 h-10 text-primary animate-spin" />
        </div>
        <Text type="supporting" color="secondary" className="text-sm text-text-muted mt-6">Loading templates...</Text>
      </div>
    );
  }

  const shown = compact ? filteredTemplates.slice(0, limit) : filteredTemplates;

  return (
    <div className="space-y-6">
      {compact ? (
        hideHeader ? null : (
          <div className="flex items-baseline justify-between gap-4">
            <h2 className="text-[15px] font-medium text-fg">
              Start from a template
            </h2>
            {headerAction}
          </div>
        )
      ) : (
        <>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted z-10"
              />
              <ToolcraftTextInputControl
                label="Search templates"
                isLabelHidden
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search templates..."
                className="pl-11 bg-background-tertiary border-border rounded-xl text-text-primary"
              />
            </div>
          </div>

          <CategoryTabs
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            categoryStats={categoryStats}
          />
        </>
      )}

      {shown.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-background-tertiary flex items-center justify-center mb-4">
            <Layers size={24} className="text-text-muted" />
          </div>
          <Text type="supporting" color="primary" weight="medium" className="text-base text-text-primary mb-1">
            No templates found
          </Text>
          <Text type="supporting" color="secondary" className="text-sm text-text-muted">
            Try adjusting your search or filter
          </Text>
        </div>
      ) : (
        <div
          className={
            compact
              ? "grid grid-cols-2 gap-4 md:grid-cols-4"
              : "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          }
        >
          {shown.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onClick={() => handleSelectTemplate(template)}
            />
          ))}
        </div>
      )}

      {showPreview && selectedTemplate && (
        <TemplatePreviewModal
          template={selectedTemplate}
          onClose={handleClosePreview}
          onApply={handleApplyTemplate}
        />
      )}
    </div>
  );
};

function mapCategoryToSocial(category: string): SocialMediaCategory {
  const mapping: Record<string, SocialMediaCategory> = {
    "social-media": "tiktok",
    youtube: "youtube-video",
    tiktok: "tiktok",
    instagram: "instagram-reels",
    business: "promo",
    personal: "custom",
    slideshow: "slideshow",
    "intro-outro": "intro",
    "lower-third": "lower-third",
    custom: "custom",
  };
  return mapping[category] || "custom";
}

export default TemplateGallery;
